// Server Side Component compatibility (specifying this cannot be used as such)
// https://beta.nextjs.org/docs/rendering/server-and-client-components#third-party-packages
'use client';
import type { State, Manager } from '@rest-hooks/core';
import { createReducer, Controller } from '@rest-hooks/core';
import useEnhancedReducer from '@rest-hooks/use-enhanced-reducer';
import type { Middleware } from '@rest-hooks/use-enhanced-reducer';
import React, { useEffect, useMemo, memo } from 'react';

import { StateContext, DispatchContext } from '../context.js';
import BackupBoundary from './BackupBoundary.js';

interface StoreProps {
  children: React.ReactNode;
  managers: Manager[];
  middlewares: Middleware[];
  initialState: State<unknown>;
  controller: Controller;
}
/**
 * This part of the provider concerns only the parts that matter for store changes
 * It expects its props to have referential stability
 */
function CacheStore({
  children,
  managers,
  middlewares,
  initialState,
  controller,
}: StoreProps) {
  const masterReducer = useMemo(() => createReducer(controller), [controller]);

  const [state, dispatch] = useEnhancedReducer(
    masterReducer,
    initialState,
    middlewares,
  );

  const optimisticState = useMemo(
    () => state.optimistic.reduce(masterReducer, state),
    [masterReducer, state],
  );

  // if we change out the manager we need to make sure it has no hanging async
  useEffect(() => {
    for (let i = 0; i < managers.length; ++i) {
      managers[i].init?.(state);
    }
    return () => {
      // dispatching after unmount should be ignored
      /* istanbul ignore else */
      if (process.env.NODE_ENV !== 'production') {
        (controller as any).dispatch = (action: any) => {
          console.debug(
            'Action dispatched after CacheProvider unmounted. This will be ignored.',
          );
          console.debug(JSON.stringify(action, undefined, 2));
          return Promise.resolve();
        };
      } else {
        (controller as any).dispatch = () => Promise.resolve();
      }
      for (let i = 0; i < managers.length; ++i) {
        managers[i].cleanup();
      }
    };
    // we're ignoring state here, because it shouldn't trigger inits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managers]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={optimisticState}>
        <BackupBoundary>{children}</BackupBoundary>
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
}
export default memo(CacheStore);
